'use client';

import { env } from '@/lib/env';
import React, { useEffect, useState, useRef } from 'react';

export interface MapResult {
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

interface JstoreMapProps {
  onResultUpdate?: (result: MapResult) => void;
  mapUrl?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

function MapServiceComponent({
  onResultUpdate,
  mapUrl = env.NEXT_PUBLIC_MAP_SERVICE,
  coordinates,
}: JstoreMapProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState('0px');

  // Handle messages from the iframe - memoized with useCallback
  const handleMessage = React.useCallback(
    (event: MessageEvent) => {
      try {
        const { type, payload } = event.data;
        if (type === 'MAP_RESULT_UPDATE' && payload) {
          const result: MapResult = {
            address: payload.label,
            coordinates: {
              lat: payload.coordinates.lat,
              lng: payload.coordinates.lng,
            },
          };

          // Call the callback if provided
          if (onResultUpdate) onResultUpdate(result);
        }
      } catch (error: any) {
        setError(error.message);
      }
    },
    [onResultUpdate]
  );

  // Set up the message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  // Send coordinates to iframe effect
  const sendCoordinates = React.useCallback(() => {
    if (!isLoading && coordinates && iframeRef.current) {
      // Add timeout to ensure iframe is ready
      setTimeout(() => {
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: 'REQUEST_UPDATE',
            payload: coordinates,
          },
          '*'
        );
      }, 1200);
    }
  }, [isLoading, coordinates]);

  // Watch for coordinate changes and send them to iframe
  useEffect(() => {
    sendCoordinates();
  }, [sendCoordinates]);

  // Handle iframe loading
  const handleIframeLoad = () => {
    setIsLoading(false);
    const iframe = iframeRef.current;
    if (iframe) {
      const iframeHeight = iframe.contentWindow?.document.body.scrollHeight;
      if (iframeHeight) setHeight(`${iframeHeight}px`);
    }
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setError('Failed to load map');
  };

  return (
    <div className='pt relative w-full'>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.8)',
            zIndex: 1,
          }}
        >
          Loading map...
        </div>
      )}

      {error && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.8)',
            color: 'red',
            zIndex: 1,
          }}
        >
          {error}
        </div>
      )}

      <iframe
        ref={iframeRef}
        id='jubelio-maps'
        allow='geolocation'
        src={mapUrl + '/v2'}
        width='100%'
        height={height}
        className='-mt-3 h-[420px] w-full border-none zoom-out-50'
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />
    </div>
  );
}

// Export a memoized version of the component to prevent unnecessary re-renders
const MapService = React.memo(MapServiceComponent, (prevProps, nextProps) => {
  // Only re-render if the coordinates or callback actually changed
  const coordsEqual =
    prevProps.coordinates?.lat === nextProps.coordinates?.lat &&
    prevProps.coordinates?.lng === nextProps.coordinates?.lng;

  // If coordinates are equal and the URL hasn't changed, prevent re-render
  return coordsEqual && prevProps.mapUrl === nextProps.mapUrl;
});

// Default export for backward compatibility
export default MapService;
